/**
 * Display rules shared by the Context card and the Context list row, kept out of the component files so
 * both views stay in step (and so the component modules only export components).
 */
import { BookOpen, Brain, FileText, type LucideIcon, ScrollText, Trash2 } from 'lucide-react';

import type { KebabMenuAction } from '@/_shared/components/list/KebabMenu';
import type { ContextHubItem } from '@/_shared/lib/context-hub';

export const KIND_ICON: Record<ContextHubItem['kind'], LucideIcon> = {
  generated: FileText,
  memory: Brain,
  note: BookOpen,
  skill: ScrollText,
};

/** A generated doc's summary is machine-written boilerplate that restates the title — it earns no room. */
export function showDescription(item: ContextHubItem): boolean {
  return item.kind !== 'generated';
}

/**
 * The source line carries the concrete origin (a repo, the chat it came from). A generated *warehouse*
 * doc's origin is the warehouse itself, already implied by its source badge, so that pairing shows nothing.
 */
export function showMeta(item: ContextHubItem): boolean {
  return Boolean(item.sourceLabel) && !(item.kind === 'generated' && item.sourceType === 'warehouse');
}

/** Delete, gated while a generated doc is still being written (the server rejects that with a conflict). */
export function contextDeleteAction(item: ContextHubItem, onDelete: (item: ContextHubItem) => void): KebabMenuAction {
  const generating = item.status === 'generating';
  return {
    label: 'Delete',
    icon: Trash2,
    destructive: true,
    disabled: generating,
    disabledReason: generating ? 'Wait for generation to finish before deleting this doc.' : undefined,
    onSelect: () => onDelete(item),
  };
}
